import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import html2canvas from 'html2canvas'
import { LineChart as LineChartIcon, Sparkles } from 'lucide-react'
import {
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts'
import type { TooltipProps } from 'recharts'
import { api } from '../../lib/api'
import {
  fetchEvidenceChartAnalysis,
  type EvidenceChartAnalysisPayload
} from '../../lib/studentEvidenceChartAnalysis'
import { isAxiosError } from 'axios'
import toast from 'react-hot-toast'

type EvidenceTrendSubject = 'ALL' | 'Deutsch' | 'Mathematik' | 'Englisch'

interface EvidenceTrendSeries {
  key: string
  label: string
  type: 'student' | 'classAverage'
  studentId?: string
}

interface EvidenceTrendData {
  range: {
    startDate: string
    endDate: string
    totalDays: number
  }
  subject: string
  series: EvidenceTrendSeries[]
  timeline: Array<Record<string, number | string>>
  summary: {
    totalBelege: number
    classAverageTotal: number | null
    studentCount: number
    selectedStudents: Array<{
      studentId: string
      label: string
      totalBelege: number
    }>
  }
}

const numberFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 })

/** Lokales Kalenderdatum YYYY-MM-DD (ohne UTC-Verschiebung). */
function toDateKeyLocal(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Beginn des laufenden Schuljahres: immer 1. August (Wechsel nach dem 31. Juli). */
function getSchoolYearStartLocal(ref = new Date()): Date {
  const month = ref.getMonth() + 1
  const year = ref.getFullYear()
  if (month >= 8) {
    return new Date(year, 7, 1)
  }
  return new Date(year - 1, 7, 1)
}

/** Ende des Schuljahres zum gegebenen 1.-August-Start: 31. Juli des Folgejahres. */
function getSchoolYearEndLocal(syStart: Date): Date {
  return new Date(syStart.getFullYear() + 1, 6, 31)
}

/** Zeitraum für das Diagramm: 1. August aktuelles Schuljahr bis heute (nicht über das Schuljahresende hinaus). */
function getEvidenceChartDateRange(): { startDate: string; endDate: string } {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const syStart = getSchoolYearStartLocal(today)
  syStart.setHours(0, 0, 0, 0)
  const syEnd = getSchoolYearEndLocal(syStart)
  syEnd.setHours(0, 0, 0, 0)
  const end = today.getTime() <= syEnd.getTime() ? today : syEnd
  return {
    startDate: toDateKeyLocal(syStart),
    endDate: toDateKeyLocal(end)
  }
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    return (error.response?.data as { message?: string })?.message || error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'Unbekannter Fehler'
}

const fetchStudentEvidenceTrend = async (
  subject: EvidenceTrendSubject,
  includeClassAverage: boolean
): Promise<EvidenceTrendData> => {
  const { startDate, endDate } = getEvidenceChartDateRange()
  const params = new URLSearchParams()
  params.set('subject', subject)
  params.set('timeScope', 'custom')
  params.set('startDate', startDate)
  params.set('endDate', endDate)
  params.set('includeClassAverage', includeClassAverage ? 'true' : 'false')
  const response = await api.get(`/student/evidence-trend?${params.toString()}`)
  const payload = response.data as { success: boolean; data: EvidenceTrendData; error?: string; message?: string }
  if (!payload.success) {
    throw new Error(payload.message || payload.error || 'Liniendiagramm konnte nicht geladen werden.')
  }
  if (!payload.data) {
    throw new Error('Unerwartete Antwort des Servers.')
  }
  return payload.data
}

const EvidenceTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null
  }
  const labelDate = label
    ? new Date(String(label)).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit'
      })
    : ''
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs sm:max-w-md">
      <p className="text-xs uppercase text-gray-500">{labelDate}</p>
      <div className="mt-2 space-y-1">
        {payload.map((entry) => (
          <div
            key={String(entry.dataKey)}
            className="flex items-center justify-between text-sm text-gray-700 gap-3"
          >
            <span className="flex items-center min-w-0">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full mr-2 border border-white shadow flex-shrink-0"
                style={{ backgroundColor: entry.color || '#6b7280' }}
              />
              <span className="truncate">{entry.name}</span>
            </span>
            <span className="font-semibold text-gray-900 flex-shrink-0 tabular-nums">
              {typeof entry.value === 'number' ? numberFormatter.format(entry.value) : entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Farben zur klaren Unterscheidung wie in der Statistik-Ansicht */
function seriesVisual(series: EvidenceTrendSeries) {
  if (series.type === 'classAverage') {
    return { stroke: '#111827', strokeDasharray: '5 5', strokeWidth: 3 as const }
  }
  return { stroke: '#dc2626', strokeDasharray: undefined as string | undefined, strokeWidth: 3 as const }
}

/**
 * Kumulativer Belege-Verlauf für den eingeloggten Schüler vs. Klassenmittelwert.
 * Zeitraum: laufendes Schuljahr vom 1. August bis zum heutigen Tag (Zeitfenster wie Backend „custom“).
 */
export default function StudentEvidenceTrendChart() {
  const chartCaptureRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const autoAttemptedKeyRef = useRef<string | null>(null)

  const [subject, setSubject] = useState<EvidenceTrendSubject>('ALL')
  const [includeClassAverage, setIncludeClassAverage] = useState(true)

  const chartDayKey = toDateKeyLocal(new Date())

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
    ['student-evidence-trend', subject, includeClassAverage, chartDayKey],
    () => fetchStudentEvidenceTrend(subject, includeClassAverage),
    { staleTime: 60 * 1000 }
  )

  const {
    data: analysisPayload,
    isLoading: analysisLoading,
    isFetching: analysisFetching
  } = useQuery(
    ['student-evidence-chart-analysis', subject, includeClassAverage],
    () => fetchEvidenceChartAnalysis(subject, includeClassAverage),
    { staleTime: 30 * 1000 }
  )

  const refreshAnalysisMutation = useMutation({
    mutationFn: async () => {
      const el = chartCaptureRef.current
      if (!el) {
        throw new Error('Diagramm nicht bereit.')
      }
      await new Promise((r) => setTimeout(r, 500))
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#f9fafb',
        logging: false
      })
      const dataUrl = canvas.toDataURL('image/png')
      const base64 = dataUrl.split(',')[1] || ''
      if (!base64) {
        throw new Error('Bildexport fehlgeschlagen.')
      }
      const response = await api.post('/student/evidence-chart-analysis/refresh', {
        imagePngBase64: base64,
        subject,
        includeClassAverage
      })
      const body = response.data as { success: boolean; data?: EvidenceChartAnalysisPayload; message?: string }
      if (!body.success || !body.data) {
        throw new Error(body.message || 'Auswertung fehlgeschlagen.')
      }
      return body.data
    },
    onSuccess: (payload) => {
      queryClient.setQueryData(['student-evidence-chart-analysis', subject, includeClassAverage], payload)
    }
  })

  const trendRangeLabel = useMemo(() => {
    if (!data?.range?.startDate || !data?.range?.endDate) {
      return ''
    }
    return `${new Date(data.range.startDate).toLocaleDateString('de-DE')} – ${new Date(
      data.range.endDate
    ).toLocaleDateString('de-DE')}`
  }, [data])

  const hasSeries = Boolean(data?.series?.length && data.timeline?.length)

  const filterKey = `${subject}|${includeClassAverage ? '1' : '0'}`

  useEffect(() => {
    autoAttemptedKeyRef.current = null
  }, [filterKey])

  useEffect(() => {
    if (!hasSeries || isLoading) {
      return
    }
    if (analysisLoading || analysisFetching) {
      return
    }
    const meta = analysisPayload
    if (!meta?.featureEnabled || !meta?.llmAvailable) {
      return
    }
    // Bedingungen „keine Analyse“ oder „älter als staleAfterDays“ kommen vom Server (GET).
    // Die Prüfung wird zusätzlich beim Schüler-Login angestoßen (Prefetch/Invalidate in useAuth).
    if (!meta.isStale && meta.analysis) {
      return
    }
    if (refreshAnalysisMutation.isLoading) {
      return
    }
    if (autoAttemptedKeyRef.current === filterKey) {
      return
    }

    autoAttemptedKeyRef.current = filterKey
    let cancelled = false
    const t = window.setTimeout(() => {
      if (cancelled) {
        return
      }
      refreshAnalysisMutation.mutate(undefined, {
        onError: (err) => {
          toast.error(getErrorMessage(err))
        }
      })
    }, 400)

    return () => {
      cancelled = true
      window.clearTimeout(t)
      if (autoAttemptedKeyRef.current === filterKey) {
        autoAttemptedKeyRef.current = null
      }
    }
  }, [
    hasSeries,
    isLoading,
    analysisLoading,
    analysisFetching,
    analysisPayload,
    subject,
    includeClassAverage,
    filterKey
  ])

  const analysisGeneratedLabel = useMemo(() => {
    if (!analysisPayload?.generatedAt) {
      return null
    }
    try {
      return new Date(analysisPayload.generatedAt).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return null
    }
  }, [analysisPayload?.generatedAt])

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <LineChartIcon className="w-5 h-5 text-primary-600 flex-shrink-0" />
            Belege über die Zeit
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Deine Entwicklung über das Schuljahr. Der Klassendurchschnitt dient zum Vergleich.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-sm px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60 self-start"
        >
          {isFetching ? 'Lädt…' : 'Aktualisieren'}
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div className="min-w-[12rem]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Fachfilter</label>
          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value as EvidenceTrendSubject)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="ALL">Alle Fächer</option>
            <option value="Deutsch">Deutsch</option>
            <option value="Mathematik">Mathematik</option>
            <option value="Englisch">Englisch</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Klassenmittelwert</label>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => setIncludeClassAverage((p) => !p)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                includeClassAverage ? 'bg-green-600' : 'bg-gray-300'
              }`}
              aria-pressed={includeClassAverage}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                  includeClassAverage ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-700">{includeClassAverage ? 'Aktiv' : 'Aus'}</span>
          </div>
        </div>
        <div className="ml-auto">
          <p className="text-xs uppercase text-gray-500 tracking-wide">Zeitraum</p>
          <p className="text-sm font-semibold text-gray-900">{trendRangeLabel || '—'}</p>
          <p className="text-xs text-gray-500">Laufendes Schuljahr · 1. August bis heute</p>
        </div>
      </div>

      <div ref={chartCaptureRef} className="h-[22rem] md:h-[28rem] border border-gray-100 rounded-lg bg-gray-50/40">
        {isLoading ? (
          <div className="flex h-full items-center justify-center space-x-2 text-gray-500">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            <span>Diagramm wird geladen…</span>
          </div>
        ) : isError ? (
          <div className="flex h-full items-center justify-center p-4 text-center text-sm text-red-700">
            {getErrorMessage(error)}
          </div>
        ) : hasSeries ? (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLineChart data={data!.timeline} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" className="text-gray-100" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) =>
                  new Date(v).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
                }
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis
                stroke="#6b7280"
                fontSize={12}
                allowDecimals
                tickFormatter={(v) => numberFormatter.format(v as number)}
              />
              <Tooltip content={<EvidenceTooltip />} />
              <Legend verticalAlign="bottom" height={36} />
              {data!.series.map((s) => {
                const vis = seriesVisual(s)
                return (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.type === 'student' ? `Deine Belege (${s.label})` : s.label}
                    stroke={vis.stroke}
                    strokeWidth={vis.strokeWidth}
                    strokeDasharray={vis.strokeDasharray}
                    dot={false}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                  />
                )
              })}
            </RechartsLineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500 px-4 text-center">
            Keine Belege im aktuellen Zeitraum für diesen Fachfilter.
          </div>
        )}
      </div>

      {(analysisLoading || refreshAnalysisMutation.isLoading) && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
          <span>KI wertet das Diagramm aus …</span>
        </div>
      )}

      {analysisPayload && !analysisPayload.featureEnabled && (
        <p className="mt-4 text-sm text-gray-500">
          Die automatische Diagramm-Auswertung ist für diese Installation oder in den KI-Einstellungen deaktiviert.
        </p>
      )}

      {analysisPayload?.featureEnabled && !analysisPayload.llmAvailable && (
        <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          KI-Auswertung ist derzeit nicht verfügbar (Hub/OpenAI nicht konfiguriert).
        </p>
      )}

      {analysisPayload?.featureEnabled &&
        analysisPayload.llmAvailable &&
        hasSeries &&
        analysisPayload.analysis && (
          <div className="mt-6 border border-gray-100 rounded-lg bg-slate-50/80 p-4">
            <div className="flex items-center gap-2 text-gray-900 font-medium mb-3">
              <Sparkles className="w-5 h-5 text-primary-600 flex-shrink-0" />
              Einordnung deines Diagramms
            </div>
            {analysisGeneratedLabel && (
              <p className="text-xs text-gray-500 mb-3">Stand: {analysisGeneratedLabel}</p>
            )}
            <div className="text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">{analysisPayload.analysis}</div>
            {analysisPayload.isStale && !refreshAnalysisMutation.isLoading && (
              <p className="mt-3 text-xs text-amber-800">
                Diese Auswertung ist älter als {analysisPayload.staleAfterDays} Tage; sie wird beim nächsten Besuch
                automatisch erneuert.
              </p>
            )}
          </div>
        )}
    </div>
  )
}
